import { useState, useEffect, useRef, ReactNode } from 'react'
import {
  Box, Divider, Typography,
} from '@mui/material'
import { addToast } from '../../services/Toasts'
import taapi from '../../services/taapi'
import type { MentionItem, ToolEvent, ConvoMessage, TokenUsage, ChatIteration, ChatSection } from '../../types'
import type { Question } from '../../types/question'
import SearchingScope from '../../comps/SearchingScope'
import ChatInputBar from '../../comps/ChatInputBar'
import { IterationGroup } from './IterationGroup'
import { AiAvatar, UsageRow, UserMessage, AssistantMessage } from './ChatMessages'
import DebugJsonButton from '../../comps/DebugJsonButton'
import { FS_XS, ICON_SM } from '../../common/theme'
import NotesIcon from '@mui/icons-material/Notes'
import { DEBUG_MODE } from '../../common/common'

// ─── Component ───────────────────────────────────────────────────────────────

interface ChatPanelProps {
  // Self-contained mode (KnowledgeBaseTab)
  currentPath?: string
  currentFolderName?: string
  onConversationChange?: (hasMessages: boolean) => void
  // Question-attached mode (ResponseBuilderTab). When set, the panel posts
  // to /api2/chatstream with { projectsha, questionsha, question, message }
  // instead of running the KB-search /api2/qastream flow.
  currentQuestion?: Question | null
  // Controlled mode (QuickChatTab) - when onExternalAsk is provided, external state drives the panel
  externalMessages?: ConvoMessage[]
  externalIsProcessing?: boolean
  externalStreamingText?: string | null
  externalStreamingTools?: ToolEvent[]
  externalStreamingThinking?: string | null
  externalStreamingIterations?: ChatIteration[] | null
  externalStreamingUsage?: TokenUsage | null
  onExternalAsk?: (q: string, mentions: MentionItem[]) => void
  onExternalStop?: () => void
  onResend?: (index: number) => void
  // Toolbar slots
  toolbarScopeContent?: ReactNode
  toolbarRightContent?: ReactNode
  // UI options
  hideCloseButton?: boolean
  emptyState?: ReactNode
  // When true, the panel always claims `flex: 1` in its parent (even with
  // no conversation), so the composer pins to the bottom and any
  // `emptyState` fills the space above. ChatTab uses this so a fresh
  // conversation lands at the bottom of the right pane.
  fillWhenEmpty?: boolean
}

const MENTION_PAGE_SIZE = 5

// Wraps /api2/kbsearch into the shape ChatInputBar expects.
async function searchKbMentions(query: string, page: number): Promise<MentionItem[]> {
  const response = await taapi.get('/api2/kbsearch', { q: query, page: String(page) })
  const raw = (response?.results ?? []) as { type: string; filename: string; sha: string; dirpath?: string }[]
  return raw.map(r => ({
    name: r.filename,
    sha: r.sha,
    type: r.type as 'file' | 'folder',
    dirpath: r.dirpath,
  }))
}

export default function ChatPanel({
  currentPath, currentFolderName, onConversationChange,
  currentQuestion,
  externalMessages, externalIsProcessing, externalStreamingText, externalStreamingTools,
  externalStreamingThinking, externalStreamingIterations, externalStreamingUsage,
  onExternalAsk, onExternalStop, onResend,
  toolbarScopeContent, toolbarRightContent, hideCloseButton, emptyState,
  fillWhenEmpty,
}: ChatPanelProps) {
  // When a question is attached we drive 
  // instead of /api2/qastream (KB search). This also disables the @-mention
  // dropdown since mentions only mean something against the KB index.
  const questionMode = !!currentQuestion

  const isControlled = !!onExternalAsk

  // Debug dialog - DEV-only inspector that mirrors the BidQuestion debug
  // button. Dumps the panel's live state (messages + streaming snapshot +

  const [messages, setMessages] = useState<ConvoMessage[]>([])
  const [isProcessing, setIsProcessing] = useState(false)
  const [streamingText, setStreamingText] = useState<string | null>(null)
  const [streamingTools, setStreamingTools] = useState<ToolEvent[]>([])

  const activeMessages = isControlled ? (externalMessages ?? []) : messages
  const activeIsProcessing = isControlled ? (externalIsProcessing ?? false) : isProcessing
  const activeStreamingText = isControlled ? (externalStreamingText ?? null) : streamingText
  const activeStreamingTools = isControlled ? (externalStreamingTools ?? []) : streamingTools
  const activeStreamingThinking = isControlled ? (externalStreamingThinking ?? null) : null
  const activeStreamingIterations = isControlled ? (externalStreamingIterations ?? null) : null
  const activeStreamingUsage = isControlled ? (externalStreamingUsage ?? null) : null

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  // "Has a conversation" - non-empty message list OR an in-flight stream.
  // Drives both the layout (collapse to composer-only when false, grow to
  // fill the parent when true) and the onConversationChange callback so
  // hosts can show/hide a resize handle for the sibling pane.
  const hasActivity = activeMessages.length > 0 || activeStreamingText !== null

  useEffect(() => {
    onConversationChange?.(hasActivity)
  }, [hasActivity])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [activeMessages.length])

  useEffect(() => {
    if (activeStreamingText !== null) messagesEndRef.current?.scrollIntoView({ behavior: 'auto' })
  }, [activeStreamingText])

  // ── Send message ─────────────────────────────────────────────────────────
  // ChatInputBar collects the editor text + @-mentions and hands them here.

  const handleAsk = async (q: string, currentMentions: MentionItem[]) => {
    if (!q || activeIsProcessing) return

    if (isControlled) {
      onExternalAsk!(q, currentMentions)
      return
    }

    setIsProcessing(true)
    setStreamingText('')
    setStreamingTools([])

    const newUserMsg: ConvoMessage = { type: 'user', q, updatetime: new Date().toISOString() }
    const outboundMessages = [...messages, newUserMsg]
    setMessages(prev => [...prev, newUserMsg])

    let fullText = ''
    let localTools: ToolEvent[] = []
    const controller = new AbortController()
    abortControllerRef.current = controller

    try {
      // Both modes use the same WebSocket transport. Question-attached chat
      // goes to /api2/chatstream with the workflow-chat payload; KB chat goes
      // to /api2/qastream with the @-mention list. The server-side WS
      // middleware intercepts both. Each yielded value is one `data:` payload.
      const lines = questionMode
        ? taapi.openStreamSocket('/api2/chatstream', {
          projectsha: currentQuestion!.projectsha,
          questionsha: currentQuestion!.questionsha,
          question: currentQuestion!.question ?? '',
          message: q,
        }, controller.signal)
        : taapi.openStreamSocket('/api2/qastream', {
          messagelist: outboundMessages,
          systemprompt: null,
          mentionedShas: currentMentions.map(m => `${m.type}:${m.sha}`),
          searchKnowledgeBase: true,
        }, controller.signal)

      for await (const data of lines) {
        if (data === '[DONE]') break
        let parsed: { text?: string; error?: string; tool?: string; status?: string; result_schema?: string; result?: any }
        try { parsed = JSON.parse(data) } catch { continue }
        if (parsed.error) throw new Error(parsed.error)
        if (parsed.tool && parsed.status) {
          const merge = (t: ToolEvent): ToolEvent => ({
            ...t,
            status: parsed.status!,
            result_schema: parsed.result_schema ?? t.result_schema,
            result: parsed.result !== undefined ? parsed.result : t.result,
          })
          localTools = localTools.map(t => t.tool === parsed.tool ? merge(t) : t)
          if (!localTools.some(t => t.tool === parsed.tool)) {
            localTools = [...localTools, {
              tool: parsed.tool,
              status: parsed.status,
              result_schema: parsed.result_schema,
              result: parsed.result,
            }]
          }
          setStreamingTools([...localTools])
        }
        if (parsed.text) { fullText += parsed.text; setStreamingText(fullText) }
      }

      setMessages(prev => [...prev, {
        type: 'assistant',
        q: fullText,
        updatetime: new Date().toISOString(),
        usessources: true,
        tools: localTools.length > 0 ? [...localTools] : undefined,
      }])
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        if (fullText) {
          setMessages(prev => [...prev, {
            type: 'assistant' as const,
            q: fullText,
            updatetime: new Date().toISOString(),
            usessources: true,
            stoppedManually: true,
            tools: localTools.length > 0 ? [...localTools] : undefined,
          }])
        }
      } else {
        addToast('error', 'Failed to get a response.')
      }
    } finally {
      abortControllerRef.current = null
      setStreamingText(null)
      setIsProcessing(false)
    }
  }

  const handleStop = () => {
    if (onExternalStop) onExternalStop()
    else abortControllerRef.current?.abort()
  }

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <Box data-testid="kcp_panel" sx={{
      display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
      minHeight: 0, overflow: 'hidden', px: 0.5, pt: 0.5,
      // No conversation yet → collapse to the composer's natural height so
      // the surrounding host can give the rest of the pane to its sibling.
      // Conversation active → grow to fill whatever space the host allots.
      // `fillWhenEmpty` forces the panel to stay at flex:1 regardless, so
      // the composer pins to the bottom of the parent pane (ChatTab).
      flex: (hasActivity || fillWhenEmpty) ? 1 : '0 0 auto',
    }}>

      {/* Messages area - rendered when there's a conversation, or when the
          host opted into `fillWhenEmpty` (so its `emptyState` fills the
          space above the composer). Otherwise omitted entirely so the
          panel collapses cleanly down to composer + divider. */}
      {(hasActivity || fillWhenEmpty) && (
        <Box data-testid="kcp_messages" sx={{ flex: 1, overflowY: 'auto', px: isControlled ? 1.5 : 0, py: isControlled ? 1 : 0 }}>

          {activeMessages.length === 0 && activeStreamingText === null && emptyState}

          {activeMessages.map((m, i) =>
            m.type === 'user'
              ? <UserMessage key={i} msg={m} index={i} isProcessing={activeIsProcessing} onResend={onResend} />
              : <AssistantMessage key={i} msg={m} index={i} isProcessing={activeIsProcessing} />
          )}

          {activeStreamingText !== null && (
            <Box data-testid="kcp_streaming" sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1.5 }}>
              <AiAvatar />
              <Box sx={{ maxWidth: '72%', minWidth: 0 }}>
                {activeStreamingIterations && activeStreamingIterations.length > 0 ? (
                  activeStreamingIterations.map((iter, idx) => {
                    const isLast = idx === activeStreamingIterations.length - 1
                    return (
                      <IterationGroup
                        key={idx}
                        iter={iter}
                        isFinal={isLast}
                        hasNext={!isLast}
                        hasPrev={idx > 0}
                        streaming={isLast}
                      />
                    )
                  })
                ) : (
                  <IterationGroup
                    iter={{
                      // ChatIteration now carries a `sections` array
                      // instead of flat thinking/tools/text fields. Build
                      // one section for the general text (id "0"), one
                      // for thinking (id "thinking"), and one per tool.
                      sections: ((): ChatSection[] => {
                        const out: ChatSection[] = []
                        if (activeStreamingThinking) {
                          out.push({ id: 'thinking', text: activeStreamingThinking })
                        }
                        if (activeStreamingText !== null) {
                          out.push({ id: '0', text: activeStreamingText })
                        }
                        activeStreamingTools.forEach((t, i) => {
                          out.push({
                            id: `${t.tool}:${i}`,
                            tool: t.tool,
                            status: t.status,
                            result_schema: t.result_schema,
                            result: t.result,
                          })
                        })
                        return out
                      })(),
                    }}
                    isFinal
                    streaming
                  />
                )}
                {/* {activeStreamingUsage && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25, px: 0.5 }}>
                    <UsageRow usage={activeStreamingUsage} />
                  </Box>
                )} */}
              </Box>
            </Box>
          )}

          <div ref={messagesEndRef} />
        </Box>
      )}
      {(hasActivity || fillWhenEmpty) && <Divider />}

      {/* Composer - shared toolbar + @-mention editor + send/stop button. */}
      <ChatInputBar
        testId="kcp_inputbar"
        onSend={handleAsk}
        onStop={handleStop}
        busy={activeIsProcessing}
        placeholder={questionMode
          ? 'Ask the workflow assistant about this question...'
          : 'Ask questions of your Knowledge Base... (@ to mention a file/folder/bid)'}
        showComposeToggle
        showCloseButton={!hideCloseButton}
        onClose={() => setMessages([])}
        toolbarStartSlot={toolbarScopeContent ?? (questionMode
          ? <Box data-testid="scope_panel" sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.4, flex: 1, minWidth: 0 }}>
            <NotesIcon sx={{ fontSize: ICON_SM, color: 'primary.main', flexShrink: 0, mt: -0.25, mr: 0.5 }} />
            <Typography variant="caption" sx={{ fontSize: FS_XS, color: 'text.secondary', mr: 0.25 }}>
              Question {currentQuestion?.numbering ?? ""} Refinement
            </Typography>
          </Box>
          : <SearchingScope currentPath={currentPath} currentDirName={currentFolderName} />
        )
        }
        toolbarEndSlot={
          <>
            {toolbarRightContent}
            {DEBUG_MODE && (
              <DebugJsonButton
                value={{
                  mode: questionMode ? 'chatstream' : 'qastream',
                  currentQuestion: currentQuestion ?? null,
                  messages: activeMessages,
                  streaming: {
                    text: activeStreamingText,
                    tools: activeStreamingTools,
                    thinking: activeStreamingThinking,
                    iterations: activeStreamingIterations,
                    usage: activeStreamingUsage,
                    isProcessing: activeIsProcessing,
                  },
                }}
                title={`Chat JSON ${questionMode ? `· Q ${currentQuestion?.questionsha ?? ''}` : '· KB'}`}
                tooltip="Inspect chat JSON (debug)"
                testIdPrefix="kcp_debug"
              />
            )}
          </>
        }
        mentionSearch={questionMode ? undefined : searchKbMentions}
        mentionPageSize={MENTION_PAGE_SIZE}
      />

    </Box >
  )
}
